import React, { useState } from "react";
import PropTypes from "prop-types";
import { lighten, makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Card from "@material-ui/core/Card";
import Box from "@material-ui/core/Box";
import Collapse from "@material-ui/core/Collapse";
import IconButton from "@material-ui/core/IconButton";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import Button from "@material-ui/core/Button";
import Alert from "@material-ui/lab/Alert";
import LinearProgress from "@material-ui/core/LinearProgress";
import Snackbar from "@material-ui/core/Snackbar";
import { fetchWithToken } from "../../../../apiUtils";

function descendingComparator(a, b, orderBy) {
  if (a[orderBy] === null) {
    return -1;
  } else if (b[orderBy] === null) {
    return 1;
  } else if (b[orderBy] < a[orderBy]) {
    return -1;
  } else if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: "syncId", label: "Data Sync Id", key: "syncId" },
  { id: "syncBy", label: "Sync By", key: "syncBy" },
  { id: "syncDate", label: "Sync Date", key: "syncDate" },
];

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell />
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="center"
            padding={"normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{ fontFamily: "Overpass", fontSize: 20 }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: "1 1 100%",
    margin: 40,
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();
  return (
    <Toolbar>
      <Typography
        className={classes.title}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        Run Site Data Sync
      </Typography>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  searchbar: {
    width: "200px",
    height: "40px",
    margin: "auto",
  },
  snack: {
    marginBottom: "50px",
  },
}));

const getDateFormat = (date) => {
  if (date == null) return;
  var newDate = date.split("T")[0];
  var dateArray = newDate.split("-");
  var year = dateArray[0].substring(2);
  var month = dateArray[1];
  var day = dateArray[2];
  var fullTime = date.split("T")[1].split(".")[0];
  var hour = fullTime.substring(0, 2);
  var finalTime;
  if (parseInt(hour) > 12) {
    if (parseInt(hour) > 21) {
      finalTime = hour - 12 + fullTime.substring(2, 5);
    } else {
      finalTime = "0" + (hour - 12) + fullTime.substring(2, 5);
    }
    finalTime += " PM";
  } else {
    finalTime = hour + fullTime.substring(2, 5);
    finalTime += " AM";
  }
  var myDate = `${month}/${day}/${year} - ${finalTime}`;
  return myDate;
};

function CollapsedRows(props) {
  const { row, index, clearAlert } = props;
  const [open, setOpen] = React.useState(false);

  return (
    <React.Fragment>
      <TableRow
        style={{ height: "60px", background: index % 2 ? "#f8f8f8" : "white" }}
      >
        <TableCell style={{ width: "5%" }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => {
              clearAlert();
              setOpen(!open);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell
          align="center"
          style={{ width: "10%", fontFamily: "Overpass" }}
        >
          {row.syncId}
        </TableCell>
        <TableCell
          align="center"
          style={{ width: "10%", fontFamily: "Overpass" }}
        >
          {row.syncBy}
        </TableCell>
        <TableCell
          align="center"
          style={{ width: "10%", fontFamily: "Overpass" }}
        >
          {getDateFormat(row.syncDate)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="h6" gutterBottom component="div">
                Sync Changes
              </Typography>
              <Table
                size="small"
                aria-label="purchases"
                style={{ width: "33%", float: "left", marginBottom: 20 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell style={{ textAlign: "center" }}>
                      Sites Added
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody
                  style={{
                    borderLeft: "solid 1px rgba(224, 224, 224, 1)",
                    borderRight: "solid 1px rgba(224, 224, 224, 1)",
                    margin: 40,
                  }}
                >
                  {row.addedSites.length > 0 ? (
                    <>
                      {row.addedSites.map((r, i) => (
                        <TableRow>
                          <TableCell
                            style={{ background: i % 2 ? "white" : "#f8f8f8" }}
                          >
                            {r.siteId} - {r.newSiteName}
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell
                        style={{
                          background: "#f8f8f8",
                          textAlign: "center",
                          fontFamily: "Overpass",
                        }}
                      >
                        None
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Table
                size="small"
                aria-label="purchases"
                style={{ width: "33%", float: "left", marginBottom: 20 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell style={{ textAlign: "center" }}>
                      Sites Updated
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody
                  style={{
                    borderLeft: "solid 1px rgba(224, 224, 224, 1)",
                    borderRight: "solid 1px rgba(224, 224, 224, 1)",
                    margin: 40,
                  }}
                >
                  {row.updatedSites.length > 0 ? (
                    <>
                      {row.updatedSites.map((r, i) => (
                        <TableRow>
                          <TableCell
                            style={{ background: i % 2 ? "#f8f8f8" : "white" }}
                          >
                            {r.siteId} - {r.newSiteName}
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell
                        style={{
                          background: "white",
                          textAlign: "center",
                          fontFamily: "Overpass",
                        }}
                      >
                        None
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Table
                size="small"
                aria-label="purchases"
                style={{ width: "33%", float: "left", marginBottom: 20 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell style={{ textAlign: "center" }}>
                      Sites Removed
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody
                  style={{
                    borderLeft: "solid 1px rgba(224, 224, 224, 1)",
                    borderRight: "solid 1px rgba(224, 224, 224, 1)",
                  }}
                >
                  {row.deletedSites.length > 0 ? (
                    <>
                      {row.deletedSites.map((r, i) => (
                        <TableRow>
                          <TableCell
                            style={{ background: i % 2 ? "white" : "#f8f8f8" }}
                          >
                            {r.siteId} - {r.siteName}
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell
                        style={{
                          background: "#f8f8f8",
                          textAlign: "center",
                          fontFamily: "Overpass",
                        }}
                      >
                        None
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

const RunSiteDataSyncTable = (props) => {
  const { data, isAdmin } = props;
  const classes = useStyles();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = React.useState("syncDate");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [rows, setRows] = useState(data);
  const [loading, setLoading] = useState(false);

  const [alertMessage, setAlertMessage] = useState({
    message: "",
    active: false,
  });

  const sendAlert = (message, varient) => {
    setAlertMessage({
      message: message,
      varient: varient,
      active: true,
    });
  };

  const clearAlert = () => {
    setAlertMessage({
      message: "",
      varient: "",
      active: false,
    });
  };

  const ManualUpdateHandler = () => {
    clearAlert();
    setLoading(true);
    var success = false;

    fetchWithToken("api/LocationService/runLocationsDataSync/")
      .then((res) => {
        if (res.ok) success = true;
        return res.json();
      })
      .then((data) => {
        sendAlert(data.returnMessage, success ? "success" : "warning");
        if (success) setRows(data.returnObject);
        setLoading(false);
      });
  };

  const handleRequestSort = (event, property) => {
    clearAlert();
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  return (
    <div className={classes.root}>
      <Card className={classes.paper}>
        <Snackbar
          className={classes.snack}
          open={alertMessage.active}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert severity={alertMessage.varient} onClose={() => clearAlert()}>
            {alertMessage.message}
          </Alert>
        </Snackbar>
        <EnhancedTableToolbar />
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={"medium"}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              setPage={setPage}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                    <CollapsedRows
                      row={row}
                      index={index}
                      clearAlert={clearAlert}
                    />
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: rows.length > rowsPerPage ? 60 * emptyRows : 60,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && (
          <>
            <div style={{ margin: "25px" }}>
              <span style={{ display: "table", margin: "0 auto" }}>
                Please Wait For Site Data Sync To Finish
              </span>
              <LinearProgress />
            </div>
          </>
        )}
        <div class="d-flex justify-content-center">
          <div class="btn-group">
            <Button
              style={{ margin: "5px" }}
              variant="contained"
              color="primary"
              onClick={ManualUpdateHandler}
              disabled={isAdmin}
            >
              Manual Update
            </Button>
          </div>
        </div>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangeRowsPerPage={(e) => {
            clearAlert();
            handleChangeRowsPerPage(e);
          }} // Material v < 4
          onRowsPerPageChange={(e) => {
            clearAlert();
            handleChangeRowsPerPage(e);
          }} // Material v > 4
          onPageChange={(e, h) => {
            clearAlert();
            handleChangePage(h);
          }} // Material v > 4
          onChangePage={(e, h) => {
            clearAlert();
            handleChangePage(h);
          }} // Material v < 4
        />
      </Card>
    </div>
  );
};

export default RunSiteDataSyncTable;
